import React, { useState } from 'react';
import { Box, Button, Collapse, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Add, Remove } from '@material-ui/icons';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import ImageIcon from '@material-ui/icons/Image';
import { DateTime } from 'luxon';
import { CouponMasterItemTableRow } from '../../../../types/couponPackMaster';
import { CommonButton, CommonInput, CommonSelect } from './CommonComponents';
import { ModalConfirmCouponPackSmartCheck } from './ModalConfirmCouponPackSmartCheck';
import { HtmlText } from '@sbgift-coupon/use';

type CouponDetailFormInput = {
  codeInput: string;
  useCount: string;
};

type PackSmartCheckTemplateProps = {
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  currentCouponSelected?: CouponMasterItemTableRow;
  setUsedQuantity?: React.Dispatch<React.SetStateAction<number>>;
  setCodeInput?: React.Dispatch<React.SetStateAction<string>>;
};

const useStyles = makeStyles((theme) => ({
  couponImageWrapper: {
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    padding: '0px 41px',
    height: '280px',
  },
  couponName: {
    backgroundColor: 'white',
    fontFamily: 'Hiragino Kaku Gothic ProN',
    fontWeight: 600,
    fontSize: '20px',
    padding: '24px 20px',
    wordBreak: 'break-all',
  },
  couponLimitInfo: {
    backgroundColor: 'white',
    fontSize: '14px',
    color: '#FF3B30',
    paddingTop: '8px',
  },
  contents: {
    backgroundColor: 'white',
    padding: '0 20px 24px 20px',
  },
  quantityRemaining: {
    fontSize: '30px',
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  actionButtonWrapper: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '24px',
    alignItems: 'center',
  },
  infoCouponWrapper: {
    backgroundColor: '#F8F8F8',
    fontSize: '14px',
    fontWeight: 300,
    gap: '24px',
    display: 'flex',
    flexDirection: 'column',
    color: '#303030',
  },
  infoCouponLabel: {
    fontWeight: 600,
  },
  infoCouponHistoryLabel: {
    fontWeight: 600,
    fontSize: '16px',
  },
  infoCouponHistoryIcon: {
    color: '#FF3B30',
    fontSize: '26px',
    cursor: 'pointer',
    margin: '16px',
  },
  infoCouponHistoryItem: {
    fontSize: '14px',
    fontWeight: 300,
    display: 'grid',
    gap: '24px',
    padding: '0px 20px',
    marginBottom: '24px',
  },
  imageDefault: {
    width: '200px',
    height: 'auto',
    color: '#d3d3d3',
  },
  historyWrapper: {
    backgroundColor: 'white',
    borderTop: '1px solid #DCDCDC',
    borderBottom: '1px solid #DCDCDC',
  },
  content: {
    padding: '0 20px 30px 20px',
    flex: 1,
  },
  commonButton: {
    width: '100%',
    height: '56px',
    borderRadius: '999px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
  },
  preLine: {
    whiteSpace: 'pre-line',
  },
}));

export const PackDetailInputSmartCheckTemplate: React.FC<PackSmartCheckTemplateProps> = (props) => {
  const { setStatus, currentCouponSelected, setUsedQuantity, setCodeInput } = props;
  const classes = useStyles();
  const [isShowHistory, setIsShowHistory] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const useCouponDetailForm = useForm<CouponDetailFormInput>({
    defaultValues: {
      useCount: '1',
      codeInput: '',
    },
    mode: 'onBlur',
  });

  const {
    formState: { errors },
    handleSubmit,
    control,
    getValues,
  } = useCouponDetailForm;

  const [usageTotal, setUsageTotal] = useState<number>(
    currentCouponSelected ? Number(currentCouponSelected.quantity) - Number(getValues('useCount')) : 0
  );

  const handleSubmitForm = async (): Promise<void> => {
    setIsConfirmModalOpen(true);
  };

  const handleConfirmModal = async (): Promise<void> => {
    const formValues = useCouponDetailForm.getValues();
    setUsedQuantity?.(Number(formValues.useCount) || 1);
    setCodeInput?.(formValues.codeInput);
    setIsConfirmModalOpen(false);
    setStatus('COMPLETED');
  };

  return (
    <>
      <FormProvider {...useCouponDetailForm}>
        <form autoComplete="off" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
          <Box>
            {/* Coupon Image Hero */}
            <Box className={classes.couponImageWrapper}>
              {currentCouponSelected?.imageUrl ? (
                <img src={currentCouponSelected?.imageUrl} alt="Product Image" style={{ maxWidth: '100%' }} />
              ) : (
                <ImageIcon className={classes.imageDefault} />
              )}
            </Box>

            <Typography className={classes.couponName}>
              {currentCouponSelected?.couponNameHtml
                ? currentCouponSelected?.couponNameHtml
                : currentCouponSelected?.couponMasterName}
            </Typography>

            <Box className={classes.contents}>
              <Box display="flex" justifyContent="space-between" alignItems="end" position="relative">
                <Controller<CouponDetailFormInput>
                  name="useCount"
                  control={control}
                  rules={{
                    required: '使う枚数必須です',
                  }}
                  render={({ field }) => (
                    <CommonSelect
                      label="使う枚数"
                      value={field.value || ''}
                      onChange={(value: string) => {
                        field.onChange(value);
                        setUsageTotal(Number(currentCouponSelected?.quantity || 0) - Number(value || 0));
                      }}
                      options={Array.from({
                        length:
                          +(currentCouponSelected?.maxCouponsPerOrder || currentCouponSelected?.quantity || 1) > 90
                            ? 90
                            : +(currentCouponSelected?.maxCouponsPerOrder || currentCouponSelected?.quantity || 1),
                      }).map((_, index) => ({
                        label: `${index + 1}`,
                        value: `${index + 1}`,
                        amount: currentCouponSelected?.couponAmount
                          ? currentCouponSelected.couponAmount * (index + 1)
                          : undefined,
                      }))}
                      required
                      error={!!errors.useCount}
                      helperText={errors.useCount ? errors.useCount.message : ''}
                    />
                  )}
                />
                <Typography
                  variant="body1"
                  component="div"
                  style={{
                    position: 'absolute',
                    right: 0,
                    bottom: currentCouponSelected?.maxCouponsPerOrder ? -4 : -10,
                  }}
                >
                  <Box textAlign="right">
                    残り
                    <Box component="span" className={classes.quantityRemaining}>
                      {usageTotal}
                    </Box>
                    枚
                  </Box>
                  {currentCouponSelected?.maxCouponsPerOrder && (
                    <Box fontSize={12} mt={-1}>
                      1回の利用上限数 {currentCouponSelected?.maxCouponsPerOrder}枚
                    </Box>
                  )}
                </Typography>
              </Box>

              <Box marginTop={'24px'}>
                <Controller<CouponDetailFormInput>
                  name="codeInput"
                  control={control}
                  rules={{
                    required: 'コードを入力してください',
                  }}
                  render={({ field }) => (
                    <CommonInput
                      label="コード入力"
                      onChange={(e) => {
                        field.onChange(e);
                      }}
                      onPaste={(e) => e.preventDefault()}
                      value={field.value}
                      required
                      placeholder="コード入力"
                      maxLength={32}
                      error={!!errors.codeInput}
                      helperText={errors.codeInput?.message}
                    />
                  )}
                />
              </Box>

              <Box className={classes.actionButtonWrapper}>
                {currentCouponSelected?.smartCheckImageUrl &&
                currentCouponSelected.smartCheckImageUrl.smartCheckUseButtonImageUrl ? (
                  <Button type="submit" style={{ padding: 0, width: '100%' }}>
                    <img
                      src={currentCouponSelected.smartCheckImageUrl.smartCheckUseButtonImageUrl}
                      alt="Smart Check Use Button Image"
                      className={classes.commonButton}
                    />
                  </Button>
                ) : (
                  <CommonButton type="submit" label="確認する" variant="primary" />
                )}
                <CommonButton
                  label="キャンセル"
                  variant="secondary"
                  onClick={() => {
                    setStatus('LIST');
                  }}
                />
              </Box>

              {currentCouponSelected?.smartCheckSetting?.smartCheckUseButtonBottomText ? (
                <Box textAlign="center" marginTop={2}>
                  <HtmlText text={currentCouponSelected.smartCheckSetting.smartCheckUseButtonBottomText} />
                </Box>
              ) : (
                <></>
              )}
            </Box>

            <Box
              className={classes.infoCouponWrapper}
              style={{
                padding:
                  currentCouponSelected?.displayValidityDate ||
                  currentCouponSelected?.information ||
                  currentCouponSelected?.telNumber
                    ? '24px 20px'
                    : undefined,
              }}
            >
              {currentCouponSelected?.displayValidityDate && (
                <Box>
                  <Typography className={classes.infoCouponLabel}>有効期限</Typography>
                  <Typography>
                    <span>
                      {currentCouponSelected?.validityStartDateTime && (
                        <>
                          {`${DateTime.fromISO(currentCouponSelected?.validityStartDateTime).toFormat(
                            'yyyy年MM月dd日 HH:mm'
                          )} から`}
                          <br />
                        </>
                      )}
                      {`${DateTime.fromISO(currentCouponSelected?.validityEndDateTime ?? '').toFormat(
                        'yyyy年MM月dd日 HH:mm'
                      )} まで`}
                    </span>
                  </Typography>
                </Box>
              )}
              {currentCouponSelected?.information && (
                <Box>
                  <Typography className={classes.infoCouponLabel}>注意事項</Typography>
                  <HtmlText text={currentCouponSelected.information} />
                </Box>
              )}
              {currentCouponSelected?.telNumber && (
                <Box>
                  <Typography className={classes.infoCouponLabel}>問い合わせ先</Typography>
                  <HtmlText text={currentCouponSelected.telNumber} />
                </Box>
              )}
            </Box>

            <Box className={classes.historyWrapper}>
              <Box display="flex" alignItems="center" justifyContent="space-between" paddingLeft="20px">
                <Typography className={classes.infoCouponHistoryLabel}>利用履歴</Typography>
                {isShowHistory ? (
                  <Remove className={classes.infoCouponHistoryIcon} onClick={() => setIsShowHistory(false)} />
                ) : (
                  <Add className={classes.infoCouponHistoryIcon} onClick={() => setIsShowHistory(true)} />
                )}
              </Box>
              <Collapse in={isShowHistory} timeout={300}>
                <Box className={classes.infoCouponHistoryItem}>
                  <Typography>利用履歴がありません</Typography>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </form>
      </FormProvider>

      {isConfirmModalOpen && currentCouponSelected && (
        <ModalConfirmCouponPackSmartCheck
          open={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleConfirmModal}
          currentCouponSelected={currentCouponSelected}
          useCount={Number(useCouponDetailForm.getValues('useCount') || 1)}
        />
      )}
    </>
  );
};
